/**
 * The typed client for this app's Worker — one function per endpoint, and the
 * SPA's only way to reach a server.
 *
 * WHY THESE THROW instead of returning a discriminated result the way
 * apps/corpus's client does. Every function here replaces a Next server
 * action, and a server action that threw rejected the caller's promise. The
 * components that call them are ported from yominoki and already handle
 * failure that way — `drill-session.tsx` wraps `gradeDrillAttempt` in a
 * try/catch and moves to a `grading_error` state, `etymology-slot.tsx`
 * `.catch`es and shows "etymology unavailable" — so a result type would mean
 * rewriting the failure handling of every surface in the port for no change in
 * behaviour. The functions that returned a SENTINEL rather than throwing
 * (`getTopSentences` → `[]`, `recordFluencyEvent` → `{ ok: false }`) keep
 * theirs, for the same reason.
 *
 * The one thing this layer adds that the server actions never had is a name
 * for a failure the conversion introduces. wrangler serves this app with
 * `not_found_handling: "single-page-application"`, so a path the Worker does
 * not claim in `run_worker_first` is answered with 200 and the HTML shell —
 * `res.ok` is true and `res.json()` throws. That is reachable whenever the
 * route list and the Worker drift (a renamed endpoint, a stale client against
 * a newer deploy), and it is nothing like the network being down, so
 * `ApiError.reason` says which it was.
 */
import type {
  AtlasEntry,
  AtlasResponse,
  Direction,
  DrillCandidatesResponse,
  DrillSelection,
  EngagementKind,
  EtymologyResponse,
  FluencyEventBody,
  FluencyEventResponse,
  GradeAxes,
  N5BatchResponse,
  N5SentencesResponse,
  PickerSlot,
  ReadingResponse,
  SentenceWithPost,
  SessionResponse,
} from "../api/contract";

/** Why a call failed, when it failed for a reason other than a bad status. */
export type ApiErrorReason =
  /** fetch itself rejected — offline, DNS, TLS, aborted. */
  | "network"
  /** A non-OK status. `status` carries it. */
  | "status"
  /** 200, but not JSON — the SPA shell answered an API path. */
  | "not_json"
  /** Content-Type said JSON and the body still would not parse. */
  | "bad_json";

export class ApiError extends Error {
  readonly reason: ApiErrorReason;
  readonly status: number | null;

  constructor(reason: ApiErrorReason, status: number | null, message: string) {
    super(message);
    this.name = "ApiError";
    this.reason = reason;
    this.status = status;
  }
}

/** Whether a response's Content-Type is JSON, parameters and casing aside. */
function isJsonResponse(res: Response): boolean {
  const contentType = res.headers.get("content-type");
  if (contentType === null) return false;
  const mediaType = contentType.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  return mediaType === "application/json" || mediaType.endsWith("+json");
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    // No Authorization header: this app's session is the HttpOnly
    // `__Host-yominoki_session` cookie, which a same-origin request carries by
    // default. `credentials` is stated anyway so a future cross-origin dev
    // proxy does not silently drop it.
    res = await fetch(path, { credentials: "same-origin", ...init });
  } catch {
    throw new ApiError("network", null, `${path}: request failed`);
  }
  if (!res.ok) {
    // The Worker's error bodies are `{ error, description? }`; read the code
    // when there is one so a caller surfacing `err.message` shows something
    // better than the status.
    let detail = String(res.status);
    if (isJsonResponse(res)) {
      const body: unknown = await res.json().catch(() => null);
      if (typeof body === "object" && body !== null && "error" in body) {
        detail = String(body.error);
      }
    }
    throw new ApiError("status", res.status, `${path}: ${detail}`);
  }
  // Checked BEFORE parsing: a 200 that is not JSON is the SPA shell, and that
  // is a routing fact worth reporting as itself rather than as a parse failure
  // that happens to look like one.
  if (!isJsonResponse(res)) {
    throw new ApiError("not_json", res.status, `${path}: served the SPA shell`);
  }
  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiError("bad_json", res.status, `${path}: unparseable body`);
  }
}

function post<T>(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<T> {
  return request<T>(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
}

// ---------------------------------------------------------------- session

/** Who the browser is. Never throws on "nobody" — that is a 200 with nulls. */
export function fetchSession(signal?: AbortSignal): Promise<SessionResponse> {
  return request<SessionResponse>("/api/session", { signal });
}

// ------------------------------------------------------------------ drill

export async function getDrillCandidates(
  direction: Direction,
  signal?: AbortSignal,
): Promise<PickerSlot[]> {
  const { slots } = await post<DrillCandidatesResponse>(
    "/api/drill/candidates",
    { direction },
    signal,
  );
  return slots;
}

export function selectDrillPost(
  sentenceId: string,
  readingId: string,
  sentenceText: string,
  tokens: { surface: string; pos: string; dictionary_form: string }[],
): Promise<DrillSelection> {
  return post<DrillSelection>("/api/drill/select", {
    sentenceId,
    readingId,
    sentenceText,
    tokens,
  });
}

export function gradeDrillAttempt(args: {
  sentenceId: string;
  direction: Direction;
  attempt: string;
  reference: string;
  focusLemmas: string[];
}): Promise<GradeAxes> {
  return post<GradeAxes>("/api/drill/grade", args);
}

export async function recordDrillOutcome(args: {
  sentenceId: string;
  readingId: string;
  direction: Direction;
  stretchLemmas: string[];
  grade: GradeAxes;
}): Promise<void> {
  await post("/api/drill/outcome", args);
}

export async function recordDrillEngagement(
  readingId: string,
  sourceUri: string | null,
  sourceCid: string | null,
  engagement: EngagementKind,
): Promise<void> {
  await post("/api/drill/engagement", {
    readingId,
    sourceUri,
    sourceCid,
    engagement,
  });
}

// --------------------------------------------------------------------- n5

export function getN5Batch(
  mapNumber: number,
  signal?: AbortSignal,
): Promise<N5BatchResponse> {
  return request<N5BatchResponse>(`/api/n5/batch?map=${String(mapNumber)}`, {
    signal,
  });
}

/**
 * Keeps `getTopSentences`' signature and its empty-array-on-refusal contract:
 * the tile that asked simply shows "no bluesky attestations yet".
 */
export async function getTopSentences(
  dictionaryForm: string,
  territory: string[],
  medianFreq: number,
): Promise<SentenceWithPost[]> {
  const { sentences } = await post<N5SentencesResponse>("/api/n5/sentences", {
    dictionaryForm,
    territory,
    medianFreq,
  });
  return sentences;
}

// ---------------------------------------------------------- fluency, etc.

/** Keeps the action's `{ ok: false, reason: "unauth" }` result shape. */
export function recordFluencyEvent(
  input: FluencyEventBody,
): Promise<FluencyEventResponse> {
  return post<FluencyEventResponse>("/api/fluency/event", input);
}

export async function getEtymologyForLemma(
  display: string,
): Promise<EtymologyResponse["entries"]> {
  const { entries } = await post<EtymologyResponse>("/api/etymology", {
    display,
  });
  return entries;
}

// -------------------------------------------------------- atlas, reading

export async function getAtlas(
  slug: string,
  signal?: AbortSignal,
): Promise<AtlasEntry[]> {
  const { entries } = await request<AtlasResponse>(
    `/api/atlas/${encodeURIComponent(slug)}`,
    { signal },
  );
  return entries;
}

export function getReading(
  id: string,
  signal?: AbortSignal,
): Promise<ReadingResponse> {
  return request<ReadingResponse>(`/api/reading/${encodeURIComponent(id)}`, {
    signal,
  });
}
